import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import styles from "./Onboarding.module.css";
import RexptLogo from "./RexptLogo";
import FalconLogo from "./FalconLogo";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import { useNavigate, useLocation } from "react-router-dom";
import { LoginWithEmailOTP } from "../../Store/apiStore";
import useUserDeviceInfo from "../../hooks/useUserDeviceInfo";
import useUTMParams from "../../hooks/useUTMParams";
import useRecaptchaV3 from "../../hooks/RecaptchaV3";
import spamDomains from "../../utils/spamDomains";
import Loader from "../Loader/Loader";
import useUser from "../../Store/Context/UserContext";
import decodeToken from "../../lib/decodeToken";
import { resolveHomeRoute } from "../../utils/resolveHomeRoute";
import useSkipDetails from "../../hooks/useSkipDetails";
import axios from "axios";
import PopUp from "../Popup/Popup";
import detectPlatform from "../../utils/detectPlatform";
import { useIPRestriction } from "../../hooks/useIPRestriction";
import REGION_BLOCK_MESSAGE from "../../alert/alert";
import { trackEvent, setGAUser, setUserProperties, fireAuthEvent } from "../../lib/analytics";
import { setReferrerAppSumoFlag } from "../../utils/apiBackend";

// ─── Apple device/browser detect helper ───────────────────────
const isAppleEligible = () => {
  const ua = window.navigator.userAgent;
  const isIOS = /iPhone|iPod|iPad/i.test(ua);
  const isMacSafari =
    /Macintosh/i.test(ua) &&
    /Safari/i.test(ua) &&
    !/Chrome|CriOS|FxiOS|EdgA/i.test(ua);
  const isIOSWebView =
    isIOS &&
    /(iphone|ipod|ipad).*applewebkit(?!.*safari)/i.test(ua.toLowerCase());
  const isIOSSafari =
    isIOS && /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|EdgA/i.test(ua);

  return isIOSSafari || isIOSWebView || isMacSafari;
};
// ──────────────────────────────────────────────────────────────

const APPSUMO_SOURCES = ["appsumo", "sumo", "app-sumo"];

const hasStoredAppSumoUtm = () => {
  try {
    const utm = JSON.parse(localStorage.getItem("utm_data") || "null");
    const source = String(utm?.utm_source || "").trim().toLowerCase();
    const campaign = String(utm?.utm_campaign || "")
      .trim()
      .toLowerCase()
      .replace(/[\s_-]/g, "");
    return (
      APPSUMO_SOURCES.includes(source) &&
      ["tier1", "tier2", "tier3"].includes(campaign)
    );
  } catch {
    return false;
  }
};

const routeIncompleteAppSumoSignup = ({ user, navigate }) => {
  // [FIX: appsumo-redeem-resume] `appsumoRedeemPending` outranks every check
  // below. It is set the moment an AppSumo user verifies their OTP and is
  // cleared ONLY when they redeem or skip (AppSumoRedeemPage), so while it is
  // still set the code is genuinely un-redeemed and the screen must come back.
  //
  // Without this, a user who reached the redeem screen and left without
  // finishing was never brought back: `verifyDetails` turns true once their
  // details are saved, and that first condition then bailed out — sending them
  // straight into the app with the code still unredeemed.
  const redeemPending =
    (() => {
      try {
        return localStorage.getItem("appsumoRedeemPending") === "true";
      } catch {
        return false;
      }
    })();

  if (user?.isTeamMember) return false;

  if (!redeemPending && (user?.verifyDetails || !hasStoredAppSumoUtm())) {
    return false;
  }

  localStorage.setItem("appsumoRedeemPending", "true");
  navigate("/appsumo-redeem", {
    replace: true,
    state: {
      afterRedeem: {
        isPaymentDone: !!user?.paymentDone,
        // [FIX: appsumo-redeem-resume] Carry the REAL onboarding state. A user
        // resuming a skipped redeem may already have finished onboarding, and
        // hardcoding false here would send them back through it after redeeming.
        isOnboardComplete: !!user?.verifyDetails,
        agentId: null,
        businessId: null,
      },
    },
  });
  return true;
};

const Onboarding = () => {
  const { user, setUser, updateUser } = useUser();
  const [currentStep, setCurrentStep] = useState(1);
  // ── Sirf animation ke liye (UI/logic wahi) ──────────────────────────────
  // stepAnim = naya step kaise aaye: "intro" (pehli baar), "right" (aage),
  // "left" (peeche). leaving = purana step abhi bahar ja raha hai; uske
  // khatam hote hi currentStep badalta hai.
  const [stepAnim, setStepAnim] = useState("intro");
  const [leaving, setLeaving] = useState(null); // { to, forward }
  const leaveTimer = useRef(null);
  useEffect(() => () => clearTimeout(leaveTimer.current), []);

  const goToStep = (step) => {
    // Chalte transition ke beech doosra tap na ghuse.
    if (leaving || step === currentStep) return;
    const forward = step > currentStep;
    setStepAnim(forward ? "right" : "left");
    setLeaving({ to: step, forward });
    // 200ms = Onboarding.module.css ke exit animation jitna.
    leaveTimer.current = setTimeout(() => {
      setCurrentStep(step);
      setLeaving(null);
    }, 200);
  };

  const stepMotionClass = leaving
    ? leaving.forward ? styles.exitLeft : styles.exitRight
    : `${styles.stepEnter} ${stepAnim === "intro" ? styles.enterIntro
      : stepAnim === "right" ? styles.enterFromRight : styles.enterFromLeft}`;
  // Step 1 aur 2 ka content alag unchai ka hai aur poora block beech me
  // center hota hai — isliye step badalte hi logo ki jagah ekdum badal jaati
  // thi (jhatka). Yahan purani aur nayi jagah ka fark naap kar logo ko wahan
  // se nayi jagah tak sarka dete hain. Layout wahi rehta hai, sirf safar
  // smooth hota hai.
  const logoBoxRef = useRef(null);
  const logoTopRef = useRef(null);
  // Poori screen ka dabba (intro me logo iske beech se nikalta hai) aur chalta
  // hua intro animation.
  const pageRef = useRef(null);
  const logoIntroAnim = useRef(null);
  useLayoutEffect(() => {
    const box = logoBoxRef.current;
    if (!box) return;
    // Intro abhi chal raha ho to use rok do: uska transform neeche ke naap ko
    // galat kar deta.
    logoIntroAnim.current?.cancel();
    logoIntroAnim.current = null;
    const top = box.getBoundingClientRect().top;
    const moved = logoTopRef.current === null ? 0 : logoTopRef.current - top;
    logoTopRef.current = top;
    if (!moved || !box.animate) return;
    box.animate(
      [{ transform: `translateY(${moved}px)` }, { transform: "translateY(0)" }],
      { duration: 420, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
    );
  }, [currentStep]);
  const [activeTip, setActiveTip] = useState(0);
  const [isEmailMode, setIsEmailMode] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const location = useLocation();
  // Auth/login screen ab /login route pe hai (purana step 3). / pe sirf step 1-2.
  const isLoginRoute = location.pathname === "/login";
  // App khulne par peeche ke gole fade-in; step dots sabse aakhir me (button
  // ke bhi baad). /login pe nahi.
  const isIntro = stepAnim === "intro" && !isLoginRoute;
  const introFadeClass = isIntro ? styles.introFade : "";
  const introDotsClass = isIntro ? styles.introDots : "";

  // App khulne par logo screen ke beech se nikalta hai — chhote se zoom hokar
  // bada hota hai, pal bhar rukta hai, phir chhota hote hue layout wali apni
  // jagah pe aa jaata hai. Sirf transform/opacity: layout me logo shuru se
  // apni asli jagah pe hi hai, isliye kuch khisakta nahi. Kul 1.3s;
  // Onboarding.module.css ka .enterIntro (--ob-base) isi ke baad shuru hota hai.
  useLayoutEffect(() => {
    const box = logoBoxRef.current;
    const page = pageRef.current;
    if (isLoginRoute || !box || !page || !box.animate) return;
    const b = box.getBoundingClientRect();
    const p = page.getBoundingClientRect();
    if (!b.width) return;
    const dy = p.top + p.height / 2 - (b.top + b.height / 2);
    // Chhoti screen pe zoom itna hi ki logo kinaron se na kate.
    const zoom = Math.max(1, Math.min(1.15, (p.width - 24) / b.width));
    const centre = `translateY(${dy}px)`;
    logoIntroAnim.current = box.animate(
      [
        { opacity: 0, transform: `${centre} scale(0.6)`, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
        { opacity: 1, transform: `${centre} scale(${zoom})`, offset: 0.42 },
        {
          opacity: 1,
          transform: `${centre} scale(${zoom})`,
          offset: 0.54,
          easing: "cubic-bezier(0.65, 0, 0.35, 1)",
        },
        { opacity: 1, transform: "translateY(0) scale(1)" },
      ],
      { duration: 1300 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isLoadingG, setLoadingG] = useState(false);
  const [isLoadingA, setLoadingA] = useState(false);
  // Google One Tap (web browsers only)
  const googleBtnRef = useRef(null);   // primary button slot
  const googleBtnRef2 = useRef(null);  // secondary icon slot
  const gsiInitialized = useRef(false);
  const [gsiReady, setGsiReady] = useState(false);
  const [showApple, setShowApple] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const referral = searchParams.get("referral") || "";
  const selectedPlan = searchParams.get("plan") || "";
  const isUser = searchParams.get("isUser") || "";
  const businessType = searchParams.get("businessType") || "";
  const token = localStorage.getItem("token");
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const [popupMessage, setPopupMessage] = useState("");
  const navigate = useNavigate();
  // Sign-in now SKIPS the /details screen: this forwards the user straight to
  // /business-step while running the details screen's background work
  // (UTM sync + ReactNative location + verifyDetails mark).
  const { skipToBusinessStep } = useSkipDetails();
  const info = useUserDeviceInfo();
  const { isBlocked, loading: ipLoading, ipInfo } = useIPRestriction(info);

  const [renderHtml, setRenderHtml] = useState(false);

  // Deactivation UX: a support team member who's been put on hold by every
  // owner is signed out and sent here with ?reason=deactivated. Surface the
  // "deactivated by the team owner" popup right away so they know why — instead
  // of a silent login screen. (Removed members land with reason=removed and
  // simply sign in fresh, so no popup for them.)
  useEffect(() => {
    const reason = new URLSearchParams(location.search).get("reason");
    if (reason === "deactivated") {
      setPopupType("failed");
      setPopupMessage(
        "Your account has been deactivated by the team owner. Please contact your team owner to reactivate."
      );
      setShowPopup(true);
    }
  }, [location.search]);

  // ─── Check device on mount ───
  useEffect(() => {
    setShowApple(isAppleEligible());
  }, []);

  // GA4: onboarding_start is NOT fired here anymore. This splash screen renders
  // BEFORE the user signs up (no app_user_id yet), so the event logged
  // anonymously and never attributed to the user; it also re-fired every time an
  // existing logged-in user passed through this screen (spurious, out-of-order
  // events). It now fires once, right after OTP verification / account creation,
  // in SignUp.js — where app_user_id is already bound. Anonymous top-of-funnel is
  // still covered by first_visit / page_view.

  useEffect(() => {
    const tokenFromParams = searchParams.get("token");
    const verifiedDetailsParam = searchParams.get("verifiedDetails");
    const verifiedDetails = verifiedDetailsParam === "true";

    let decodeTokenData = null;

    if (tokenFromParams) {
      // Preserve ONLY the integration OAuth params across the wipe. These are
      // NOT user data (they're the provider's callback URL + CSRF state +
      // which platform), so keeping them does not defeat the clean-slate
      // purpose of clear() — which exists to drop a PREVIOUS user's data on a
      // fresh external (e.g. Google) login. Without this, Google login wipes
      // oauth_redirect_uri and the user can never be returned to Make/Zapier.
      const oauthRedirectUri = localStorage.getItem("oauth_redirect_uri");
      const oauthState = localStorage.getItem("oauth_state");
      const oauthPlatform = localStorage.getItem("oauth_platform");
      // Per-window Make popup tag — only the Make consent popup has it; a normal
      // onboarding tab reads null and keeps nothing. Preserve it so a brand-new user
      // onboarding INSIDE the Make popup still auto-returns to Make.
      const makePopupTag = sessionStorage.getItem("make_oauth_popup");
      // [FIX: appsumo-redeem-resume] Preserve the AppSumo signup state across
      // this wipe, for the same reason the OAuth keys above are preserved: it is
      // not a previous user's data, it is THIS signup's un-redeemed code. Losing
      // utm_data here means the tier is gone, and losing the pending flag means
      // the redeem screen never comes back — the user lands in the app with the
      // code still unredeemed and no way to reach the screen again.
      const appsumoUtm = localStorage.getItem("utm_data");
      const appsumoPending = localStorage.getItem("appsumoRedeemPending");

      localStorage.clear();
      sessionStorage.clear();
      if (makePopupTag) sessionStorage.setItem("make_oauth_popup", makePopupTag);
      if (appsumoUtm) localStorage.setItem("utm_data", appsumoUtm);
      if (appsumoPending === "true")
        localStorage.setItem("appsumoRedeemPending", appsumoPending);
      decodeTokenData = decodeToken(tokenFromParams);
      localStorage.setItem("token", tokenFromParams);
      localStorage.setItem("onboardComplete", verifiedDetailsParam);

      if (oauthRedirectUri)
        localStorage.setItem("oauth_redirect_uri", oauthRedirectUri);
      if (oauthState) localStorage.setItem("oauth_state", oauthState);
      if (oauthPlatform) localStorage.setItem("oauth_platform", oauthPlatform);

      window.history.replaceState({}, "", window.location.pathname);

      // GA4: token handoff from the OAuth redirect flow — identify the user and
      // record signup/login. verifiedDetails=true => returning user (login);
      // false => brand-new signup. paymentDone isn't in the token, so only
      // stamp the free baseline on new signups (never downgrade a paid user
      // on login).
      setGAUser(decodeTokenData?.id);
      if (!verifiedDetails) {
        setUserProperties({ account_type: "free_test", plan: "none" });
      }
      // Surface-agnostic + once-per-token (works on web ?token= and app deep link).
      fireAuthEvent({ isExisting: verifiedDetails, method: "google", token: tokenFromParams });
    }

    const hasToken = localStorage.getItem("token");
    if (!hasToken) return;

    // NOTE: we do NOT return to Make here. Google/Jobber routes into the app the
    // SAME way as email — a new / half-onboarded user goes to /details to create
    // an agent first. The return to Make happens later from FastAgentDetails, and
    // ONLY once an agent exists (count >= 1). The preserved make_oauth_popup tag
    // makes that later return possible.
    if (!verifiedDetails) {
      // Skip the details screen — straight to /business-step (background runs).
      skipToBusinessStep({ replace: true, email: user?.email });
      return;
    }

    // Partial merge so the persisted `profile` URL survives — see
    // matching note in Start.js for the flicker root cause.
    if (decodeTokenData) {
      updateUser({ name: decodeTokenData?.name });
    }

    // Route by the REAL agent count so a single-agent user always lands on
    // /fast-agent-detail — never /dashboard. Deciding on the token role alone
    // sent single-agent admins/owners to /dashboard on this return path while
    // fresh login sent them to /fast-agent-detail (the "sometimes" bug).
    const dRoute = decodeTokenData || decodeToken(hasToken);
    resolveHomeRoute(dRoute).then((path) => navigate(path, { replace: true }));
  }, []);

  useEffect(() => {
    const existingToken = localStorage.getItem("token");
    if (existingToken) {
      const d = decodeToken(existingToken);
      resolveHomeRoute(d).then((path) => navigate(path));
    }
  }, []);

  useEffect(() => {
    const handleReferral = async () => {
      const currentDomain = window.location.hostname;
      const isFromReferrerLink =
        currentDomain === "refer.rxpt.us" || currentDomain === "refer.rxpt.ca";

      if (referral && isFromReferrerLink) {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/endusers/validate-referral/${referral}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            },
          );
          if (res?.data?.valid) {
            sessionStorage.setItem("referredBy", referral);
            sessionStorage.setItem("referredByName", res?.data?.referalName);
            // Only an AppSumo partner's referral routes the new signup to the
            // Python backend (isPythonBackend reads this). The visitor is an
            // ordinary retail customer either way; what matters is that the
            // plan they buy funds their referrer's monthly minutes, and that
            // accounting lives on the Python side.
            setReferrerAppSumoFlag(res?.data?.referrerIsAppSumo);
            window.location.href = `https://app.rxpt.ai?referral=${referral}`;
            setCurrentStep(3);
          } else {
            window.location.href = `https://app.rxpt.ai/`;
            sessionStorage.removeItem("referredBy");
            sessionStorage.removeItem("referredByName");
          }
        } catch (error) {
          console.error("Failed to validate referral:", error);
        }
      }

      let updated = false;
      let goToLogin = false;

      if (referral && !isFromReferrerLink) {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/endusers/check-code/${referral}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            },
          );
          if (res?.data?.valid) {
            sessionStorage.setItem("referredBy", referral);
            sessionStorage.setItem("referredByName", res?.data?.referalName);
            // Only an AppSumo partner's referral routes the new signup to the
            // Python backend (isPythonBackend reads this). The visitor is an
            // ordinary retail customer either way; what matters is that the
            // plan they buy funds their referrer's monthly minutes, and that
            // accounting lives on the Python side.
            setReferrerAppSumoFlag(res?.data?.referrerIsAppSumo);
            searchParams.delete("referral");
            goToLogin = true; // valid referral → seedha auth/login screen
            updated = true;
          } else {
            sessionStorage.removeItem("referredBy");
            sessionStorage.removeItem("referredByName");
          }
        } catch (error) {
          sessionStorage.removeItem("referredBy");
          sessionStorage.removeItem("referredByName");
        }
      }

      if (selectedPlan) {
        sessionStorage.setItem("selectedPlan", selectedPlan);
        searchParams.delete("plan");
        updated = true;
      }
      if (businessType) {
        sessionStorage.setItem("businessType", businessType);
        searchParams.delete("businessType");
        updated = true;
      }
      if (isUser === "free") {
        sessionStorage.setItem("isUser", "true");
        searchParams.delete("isUser");
      }

      if (goToLogin) {
        navigate("/login", { replace: true });
      } else if (updated) {
        navigate(location.pathname, { replace: true });
      }
    };

    handleReferral();
  }, [
    referral,
    selectedPlan,
    businessType,
    location.pathname,
    location.search,
    navigate,
  ]);
  const blockIfRegionNotAllowed = () => {
    if (!isBlocked) return false;
    setPopupType("failed");
    setRenderHtml(false);
    setPopupMessage(REGION_BLOCK_MESSAGE);
    setShowPopup(true);
    return true;
  };
  const chatkefun = () => {
    if (blockIfRegionNotAllowed()) return;
    trackEvent("sign_up_start", { method: "google" });
    setLoadingG(true);
    sessionStorage.setItem("googleAuthStartTime", Date.now().toString());
    // Track which auth method user chose — Details.js reads this to decide if email is mandatory
    localStorage.setItem("signInMethod", "google");

    // Tell the backend which surface initiated the OAuth so EndUser.platform
    // gets stamped on the callback. SignUp.js already does this; without it
    // the Onboarding-screen Google flow created users with platform=null.
    const platform = detectPlatform();

    if (window.ReactNativeWebView) {
      const googleAuthUrl = `${process.env.REACT_APP_API_BASE_URL}/auth/google?isMobile=true&flow=login&platform=${platform}`;
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: "GOOGLE_SIGN_IN", url: googleAuthUrl }),
      );
    } else {
      const isAppDomain = window.location.hostname === "app.rxpt.ai";
      const flow = isAppDomain ? "next" : "";
      const base = `${process.env.REACT_APP_API_BASE_URL}/auth/google`;
      // [FIX: origin-redirect] — do frontend (3000/3001, live app.rexpt.in/
      // app.rxpt.ai) me login ke baad usi frontend pe wapas laane ke liye origin
      // bhejo. REVERT: neeche `&origin=...` hata do.
      const originQs = `&origin=${encodeURIComponent(window.location.origin)}`;
      const url = flow
        ? `${base}?flow=${flow}&platform=${platform}${originQs}`
        : `${base}?platform=${platform}${originQs}`;

      window.location.href = url;
    }
  };

  // ─── Google One Tap (web browsers only) ─────────────────────────────
  // App WebView mein One Tap support nahi karta — wahan purana redirect
  // flow (chatkefun) hi chalega. Browser mein One Tap / GIS button.
  const routeAfterGoogleLogin = async (authToken) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/auth/getUserDetails`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const gUser = res.data.user;

      // GA4: identify the user and record signup/login (Google flow). Mirrors
      // SignUp.js — Onboarding is the root auth entry, so without this the
      // sign_up/login Key event was never firing for Google logins here.
      const gaUserId = decodeToken(authToken)?.id;
      setGAUser(gaUserId);
      setUserProperties({
        account_type: gUser?.paymentDone ? "paid" : "free_test",
        ...(gUser?.paymentDone ? {} : { plan: "none" }),
      });
      fireAuthEvent({ isExisting: gUser?.verifyDetails, method: "google", token: authToken });

      setUser({
        name: gUser?.name || "",
        profile: gUser?.profile || "images/camera-icon.avif",
        subscriptionDetails: {},
      });
      if (gUser?.email) localStorage.setItem("userEmail", gUser.email);

      // Do NOT return to Make here. Google routes into the app the SAME way as the
      // email/OTP flow: verifyDetails=false → /details (start/resume onboarding,
      // create an agent), verifyDetails=true → /fast-agent-detail. The return to
      // Make happens later from FastAgentDetails, ONLY once an agent exists
      // (count >= 1) — so a brand-new / half-onboarded user is never bounced back
      // to Make with zero agents ("No data available"). The preserved per-window
      // make_oauth_popup tag lets FastAgentDetails close the loop at the right time.

      // Team member — onboarding skip → grouped Dashboard (multi-owner)
      if (gUser?.isTeamMember) {
        localStorage.setItem("onboardComplete", "true");
        if (gUser?.ownerId) localStorage.setItem("teamOwnerId", gUser.ownerId);
        // Access picker first — see SignUp.js. It self-skips for a single
        // context, so this adds no step for most members.
        navigate("/select-access", { replace: true });
        return;
      }

      // This login no longer has any active owner membership. Clear a stale
      // team hint so the person follows the normal new-user flow.
      localStorage.removeItem("teamOwnerId");

      localStorage.setItem(
        "onboardComplete",
        gUser?.verifyDetails ? "true" : "false"
      );
      if (routeIncompleteAppSumoSignup({ user: gUser, navigate })) {
        return;
      }
      if (gUser?.verifyDetails) {
        navigate("/fast-agent-detail", { replace: true });
      } else {
        // Skip the details screen — straight to /business-step (background runs).
        skipToBusinessStep({ replace: true, email: gUser?.email });
      }
    } catch (err) {
      console.error("❌ Google login routing error:", err);
      setLoadingG(false);
      setPopupType("failed");
      setRenderHtml(false);
      setPopupMessage("Something went wrong. Please try again.");
      setShowPopup(true);
    }
  };

  // One Tap / GIS credential callback — `credential` ek Google ID token hai,
  // jise existing /auth/google/native endpoint pe verify karwate hain. Wahi
  // endpoint customerId / ZapApikey / client_id bana ke save karta hai aur
  // app ka JWT return karta hai (login pe Calendar/Contacts permission nahi).
  const handleGoogleCredential = async (response) => {
    const credential = response?.credential;
    if (!credential) return;

    if (blockIfRegionNotAllowed()) return;

    setLoadingG(true);
    localStorage.setItem("signInMethod", "google");
    try {
      const platform = detectPlatform();
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/google/native`,
        { idToken: credential, platform }
      );

      const authToken = res?.data?.token;
      if (!authToken) throw new Error("No token in response");

      // Preserve the Make OAuth popup tag across this wipe — same guarded pattern
      // as the token-in-URL path above and the OTP flow. This is the One Tap /
      // GIS path; without it a Google One Tap sign-in INSIDE the Make popup loses
      // the per-window tag and returnToProvider() (below) refuses to redirect
      // back to Make. A normal login tab has no tag → restores nothing → clears
      // fully as before.
      const makePopupTag = sessionStorage.getItem("make_oauth_popup");
      sessionStorage.clear();
      if (makePopupTag) sessionStorage.setItem("make_oauth_popup", makePopupTag);
      localStorage.setItem("token", authToken);
      localStorage.setItem(
        "onboardComplete",
        res?.data?.verifiedDetails || "false"
      );

      // [FIX: origin-redirect] Google One Tap / GIS on the LOGIN page (Onboarding
      // renders /login). SAME behaviour as the OTP + SignUp flows: if this
      // account's stored home domain (appOrigin) differs from the domain we're on
      // now, send them to THEIR domain (carrying the token via /signup?token=,
      // which routes cleanly). Clear this domain's identity keys first so no ghost
      // session lingers here. null appOrigin (new/pre-feature) → skipped. Same
      // domain → skipped (no reload).
      const homeOrigin = res?.data?.appOrigin || res?.data?.defaultAppOrigin || null;
      if (
        homeOrigin &&
        typeof window !== "undefined" &&
        homeOrigin !== window.location.origin
      ) {
        const dest = new URL("/signup", homeOrigin);
        dest.searchParams.set("token", authToken);
        dest.searchParams.set(
          "verifiedDetails",
          String(res?.data?.verifiedDetails === "true" || res?.data?.verifiedDetails === true)
        );
        [
          "token",
          "userEmail",
          "onboardComplete",
          "paymentDone",
          "userId",
          "customerId",
          "businessId",
          "fetchEmail",
          "tokenFromAgentId",
          "teamOwnerId",
          "appsumoRedeemPending",
        ].forEach((k) => {
          try {
            localStorage.removeItem(k);
          } catch (_) { }
        });
        window.location.replace(dest.toString());
        return; // stop — the target domain takes over
      }

      await routeAfterGoogleLogin(authToken);
    } catch (err) {
      console.error("❌ Google One Tap login failed:", err);
      setLoadingG(false);
      setPopupType("failed");
      setRenderHtml(false);
      setPopupMessage(
        err?.response?.data?.message || "Google login failed. Please try again."
      );
      setShowPopup(true);
    }
  };

  // Latest-handler ref so the GIS callback always reads fresh state.
  const credentialHandlerRef = useRef(handleGoogleCredential);
  credentialHandlerRef.current = handleGoogleCredential;

  // Load Google Identity Services + One Tap prompt (browser only).
  useEffect(() => {
    // [TEMP DIAGNOSTIC] — confirm naya code load hua. Test ke baad hata denge.
    console.log(
      "🟢 ONBOARDING ONE-TAP CODE LOADED | isWebView:",
      !!window.ReactNativeWebView,
      "| clientId set:",
      !!process.env.REACT_APP_GOOGLE_CLIENT_ID
    );
    if (window.ReactNativeWebView) return; // app WebView → chatkefun
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.warn(
        "REACT_APP_GOOGLE_CLIENT_ID missing — Google One Tap disabled"
      );
      return;
    }

    const initGsi = () => {
      if (!window.google?.accounts?.id || gsiInitialized.current) return;
      gsiInitialized.current = true;

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (resp) => credentialHandlerRef.current(resp),
        use_fedcm_for_prompt: true,
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      // Popup yahan se NAHI — neeche wale effect se, taaki / → /login navigate
      // pe (component remount nahi hota) bhi sahi fire ho.
      setGsiReady(true);
    };

    const existing = document.getElementById("google-gsi-script");
    if (existing) {
      if (window.google?.accounts?.id) initGsi();
      else existing.addEventListener("load", initGsi);
    } else {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.id = "google-gsi-script";
      script.onload = initGsi;
      document.body.appendChild(script);
    }
  }, []);

  // One Tap auto popup — SIRF /login route pe, aur sirf jab user logged-in NA
  // ho. / (home, step 1-2) pe popup bilkul nahi aayega.
  useEffect(() => {
    if (!gsiReady || !window.google?.accounts?.id) return;
    if (!isLoginRoute) return;
    if (localStorage.getItem("token")) return;
    window.google.accounts.id.prompt();
  }, [gsiReady, isLoginRoute]);

  // Render the GIS "Continue with Google" button(s) into whichever slots are
  // mounted. Re-runs when GIS becomes ready or when button visibility toggles.
  //
  // Glitch fix: GIS pehle generic "Continue with Google" render karta hai, phir
  // logged-in session detect hote hi personalized "Continue as <name>" chip pe
  // morph karta hai. Pehle ye effect har state change pe innerHTML clear karke
  // dobara render karta tha → har baar wahi morph flash dikhta tha. Ab:
  //   1. Slot already-rendered ho to dobara render NAHI karte (no clear/flash).
  //   2. Fresh render ko thodi der opacity:0 rakhte hain taaki generic→personalized
  //      morph user ko na dikhe; settle hone ke baad fade-in. Chip + One Tap dono
  //      waise ke waise rehte hain — sirf flicker hatta hai.
  useEffect(() => {
    if (!gsiReady || !window.google?.accounts?.id) return;
    const opts = {
      type: "standard",
      theme: "outline",
      size: "large",
      shape: "pill",
      text: "continue_with",
      logo_alignment: "center",
    };
    const timers = [];
    [googleBtnRef.current, googleBtnRef2.current].forEach((el) => {
      if (!el) return;
      // Already rendered into this slot → chhedo mat (warna morph dobara flash hoga)
      if (el.dataset.gisRendered === "1") return;

      // Layout shift roko (button render hone tak jagah reserve), aur morph
      // settle hone tak hide rakho.
      el.style.minHeight = "44px";
      el.style.opacity = "0";
      el.style.transition = "opacity 150ms ease";

      window.google.accounts.id.renderButton(el, opts);
      el.dataset.gisRendered = "1";

      // GIS ko generic→personalized chip pe settle hone do, phir reveal.
      timers.push(
        setTimeout(() => {
          el.style.opacity = "1";
        }, 450)
      );
    });
    return () => timers.forEach(clearTimeout);
  }, [gsiReady, showApple, isEmailMode, isLoadingG, currentStep, isLoginRoute]);

  const handleAppleSignIn = () => {
    if (blockIfRegionNotAllowed()) return;
    if (isLoadingA) return;
    trackEvent("sign_up_start", { method: "apple" });
    setLoadingA(true);
    sessionStorage.setItem("appleAuthStartTime", Date.now().toString());
    // Track which auth method user chose — Details.js reads this to enforce email if Apple hides it
    localStorage.setItem("signInMethod", "apple");

    const platform = detectPlatform();

    if (window.ReactNativeWebView) {
      const appleAuth = `${process.env.REACT_APP_API_BASE_URL}/auth/apple?mobile=true&flow=login&platform=${platform}`;
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: "APPLE_SIGN_IN", url: appleAuth }),
      );
    } else {
      window.location.href = `${process.env.REACT_APP_API_BASE_URL}/auth/apple?mobile=false&flow=login&platform=${platform}`;
    }
  };

  // ─── Apple loading reset ───────────────────────────────────
  useEffect(() => {
    // Reset on mount (page wapas aaya)
    setLoadingA(false);
    sessionStorage.removeItem("appleAuthStartTime");

    // pageshow — browser back/forward cache se wapas aane pe
    const handlePageShow = (e) => {
      if (e.persisted) {
        setLoadingA(false);
        sessionStorage.removeItem("appleAuthStartTime");
      }
    };

    // visibilitychange — user Apple auth se cancel karke wapas aaya
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        setLoadingA(false);
        sessionStorage.removeItem("appleAuthStartTime");
      }
    };

    // focus — window focus wapas aaya (Safari pe kaam karta hai)
    const handleFocus = () => {
      setLoadingA(false);
      sessionStorage.removeItem("appleAuthStartTime");
    };

    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);
  // ──────────────────────────────────────────────────────────

  // ─── Google loading reset ──────────────────────────────────
  useEffect(() => {
    const authStartTime = sessionStorage.getItem("googleAuthStartTime");
    if (authStartTime) {
      const elapsed = Date.now() - parseInt(authStartTime);
      if (elapsed > 2000) setLoadingG(false);
      sessionStorage.removeItem("googleAuthStartTime");
    } else {
      setLoadingG(false);
    }

    const handlePageShow = (event) => {
      if (event.persisted) {
        setLoadingG(false);
        sessionStorage.removeItem("googleAuthStartTime");
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  // ─── RN OAUTH_CANCELLED bridge ────────────────────────────
  // Inside the React Native WebView, `pageshow` and `visibilitychange`
  // never fire when InAppBrowser is dismissed (the WebView itself
  // doesn't reload). Without this listener the Google / Apple
  // button spinner stays true forever after the user backs out of
  // the OAuth screen. App.tsx posts OAUTH_CANCELLED on dismissal.
  useEffect(() => {
    const handleRNMessage = (event) => {
      try {
        const msg =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (!msg?.__fromRN) return;
        if (msg.type !== "OAUTH_CANCELLED") return;

        const provider = msg.data?.provider;
        const flow = msg.data?.flow;
        // Only reset the login-screen spinners — the contacts / calendar
        // popups have their own GOOGLE_AUTH_CANCELLED listeners.
        if (flow && flow !== "login") return;
        if (provider === "google") {
          setLoadingG(false);
          sessionStorage.removeItem("googleAuthStartTime");
        } else if (provider === "apple") {
          setLoadingA(false);
          sessionStorage.removeItem("appleAuthStartTime");
        } else {
          setLoadingG(false);
          setLoadingA(false);
        }
      } catch (_e) { /* ignore non-JSON RN messages */ }
    };

    window.addEventListener("message", handleRNMessage);
    document.addEventListener("message", handleRNMessage);
    return () => {
      window.removeEventListener("message", handleRNMessage);
      document.removeEventListener("message", handleRNMessage);
    };
  }, []);
  // ──────────────────────────────────────────────────────────

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stepFromUrl = Number(params.get("step"));
    // Purana /?step=3 ab /login pe redirect — backward compat (poore app mein
    // login redirect target /?step=3 hi tha).
    if (stepFromUrl === 3) {
      navigate("/login", { replace: true });
      return;
    }
    if (stepFromUrl === 1 || stepFromUrl === 2) {
      setCurrentStep(stepFromUrl);
    }
  }, [location.search, navigate]);

  const validateEmail = (email) => {
    if (!email) return "Email is required.";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email address.";
    return "";
  };


  const utm_data = useUTMParams();
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const executeRecaptcha = useRecaptchaV3(siteKey);

  const handleSendOtpFromOnboarding = async () => {
    const err = validateEmail(email);
    if (err) {
      setEmailError(err);
      return;
    }

    const isSpamEmail = spamDomains.some((d) => email.includes(d));
    if (isSpamEmail) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    // GA4: "Send One Time Password" pressed with a valid email — the real
    // start of the email/OTP auth path. (Invalid/spam emails return above.)
    trackEvent("send_otp_click", { screen: "login" });

    setIsSendingOtp(true);
    try {
      const recaptchaToken = await executeRecaptcha("send_otp");
      if (!recaptchaToken) {
        setEmailError("reCAPTCHA failed. Please refresh and try again.");
        return;
      }
      const response = await LoginWithEmailOTP(
        email,
        info,
        utm_data || null,
        recaptchaToken,
      );
      if (response?.status === 200) {
        // UTM already delivered to backend in this LoginWithEmailOTP call —
        // mark synced so Details.js won't re-send it for OTP users.
        if (utm_data) localStorage.setItem("utmSynced", "true");
        localStorage.setItem("userEmail", email);
        // Track auth method — email path means we already have email, no need to re-enforce
        localStorage.setItem("signInMethod", "email");
        setShowPopup(true);
        setPopupType("success");
        setPopupMessage("One Time Password sent successfully!");
        setTimeout(() => {
          navigate(`/signup?email=${encodeURIComponent(email)}`, {
            replace: true,
          });
        }, 1000);
      } else {
        setEmailError("Failed to send OTP. Please try again.");
      }
    } catch (error) {
      const msg = error?.response?.data?.error || "";
      if (
        error?.response?.status === 409 ||
        msg.toLowerCase().includes("otp already sent")
      ) {
        // UTM delivered on the prior OTP request — treat as synced.
        if (utm_data) localStorage.setItem("utmSynced", "true");
        localStorage.setItem("userEmail", email);
        navigate(
          `/signup?email=${encodeURIComponent(email)}&msg=${encodeURIComponent(msg || "OTP already sent. Please check your email.")}&type=failed`,
          { replace: true },
        );
        return;
      }
      setEmailError(msg || "Failed to send OTP. Please try again.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleNextStep = () => {
    // / pe sirf 2 steps. Step 2 ke baad auth screen ab /login route pe.
    if (currentStep === 1) {
      // GA4: "How it Works" pressed on intro slide 1.
      trackEvent("how_it_works_click", { screen: "onboarding_step1" });
      goToStep(2);
    } else if (currentStep === 2) {
      // GA4: "Build My Receptionist" pressed on intro slide 2 → auth screen.
      trackEvent("build_my_receptionist_click", { screen: "onboarding_step2" });
      navigate("/login");
    }
  };

  const tooltips = [
    { id: 1, text: "Works with your business number", position: "topLeft" },
    { id: 2, text: "You stay in control", position: "right" },
    { id: 3, text: "Sounds natural and professional", position: "bottom" },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTip((prev) => (prev + 1) % tooltips.length);
    }, 2500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className={styles.pageWrapper} ref={pageRef}>
      <div className={styles.containerBox}>
        <div className={styles.container}>
          <div className={styles.Logo} ref={logoBoxRef}>
            {/* Dono steps ka logo ek hi <img>: pehle har step ka apna tha, to
                step badalne par purana hat kar naya banta tha. Class wahi hain
                (logoImg / logoImg2), bas element ab bana rehta hai. */}
            {!isLoginRoute && (currentStep === 1 || currentStep === 2) && (
              <RexptLogo
                className={currentStep === 1 ? styles.logoImg : styles.logoImg2}
              />
            )}
            {/*
              Ripple animation (commented out per Figma — keep for future use)
              {currentStep === 2 && (
                <div className={styles.wrapper2}>
                  <div className={styles.rippleBox}>
                    {[...Array(6)].map((_, index) => (
                      <span
                        key={index}
                        className={styles.ripple}
                        style={{ animationDelay: `${index * 0.6}s` }}
                      />
                    ))}
                    <div className={styles.centerIcon}>
                      <img src="/svg/ripple-watch.svg" alt="ripple-watch" />
                    </div>
                    {tooltips.map((tip, index) => (
                      <div
                        key={index}
                        className={`${styles.tooltip} ${styles[tip.position]} ${activeTip === index ? styles.tooltipActive : ""}`}
                      >
                        {tip.text}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            */}
            {isLoginRoute && (
              <FalconLogo className={`${styles.flconIcon} ${styles.authFadeIn}`} />
            )}
          </div>
          {/* Step 1 */}
          {!isLoginRoute && currentStep === 1 && (
            <div className={`${styles.contentBox} ${stepMotionClass}`}>
              <div className={styles.wrapper}>
                <div className={styles.titlBar}>
                  <h4>Never Miss a Call</h4>
                  <h5>Never Miss a Client</h5>
                </div>
                {/* <div className={styles.decBar}>
                  <p>
                    No hardware. No technical setup. Hear how it answers your calls — <span>then decide</span>
                  </p>
                </div> */}
                <ul className={styles.bulletList}>
                  <li>
                    <span className={styles.bulletCheck} aria-hidden="true">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M27.8846 13.9423C27.8846 21.6424 21.6424 27.8846 13.9423 27.8846C6.24218 27.8846 0 21.6424 0 13.9423C0 6.24218 6.24218 0 13.9423 0C21.6424 0 27.8846 6.24218 27.8846 13.9423ZM20.9663 8.66108C20.4559 8.15063 19.6283 8.15063 19.1178 8.66108C19.1055 8.67341 19.0939 8.68647 19.0831 8.70018L13.0315 16.4114L9.38296 12.7628C8.87251 12.2524 8.0449 12.2524 7.53445 12.7628C7.024 13.2733 7.024 14.1009 7.53445 14.6113L12.1466 19.2235C12.6571 19.734 13.4847 19.734 13.9952 19.2235C14.0065 19.2122 14.0173 19.2002 14.0273 19.1876L20.9849 10.4907C21.4767 9.97889 21.4705 9.16527 20.9663 8.66108Z" fill="#6524EB" />
                      </svg>
                    </span>
                    <span className={styles.itemText}>Answers Calls</span>
                  </li>
                  <li>
                    <span className={styles.bulletCheck} aria-hidden="true"><svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M27.8846 13.9423C27.8846 21.6424 21.6424 27.8846 13.9423 27.8846C6.24218 27.8846 0 21.6424 0 13.9423C0 6.24218 6.24218 0 13.9423 0C21.6424 0 27.8846 6.24218 27.8846 13.9423ZM20.9663 8.66108C20.4559 8.15063 19.6283 8.15063 19.1178 8.66108C19.1055 8.67341 19.0939 8.68647 19.0831 8.70018L13.0315 16.4114L9.38296 12.7628C8.87251 12.2524 8.0449 12.2524 7.53445 12.7628C7.024 13.2733 7.024 14.1009 7.53445 14.6113L12.1466 19.2235C12.6571 19.734 13.4847 19.734 13.9952 19.2235C14.0065 19.2122 14.0173 19.2002 14.0273 19.1876L20.9849 10.4907C21.4767 9.97889 21.4705 9.16527 20.9663 8.66108Z" fill="#6524EB" />
                    </svg></span>
                    <span className={styles.itemText}>Books Appointments</span>
                  </li>
                  <li>
                    <span className={styles.bulletCheck} aria-hidden="true"><svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M27.8846 13.9423C27.8846 21.6424 21.6424 27.8846 13.9423 27.8846C6.24218 27.8846 0 21.6424 0 13.9423C0 6.24218 6.24218 0 13.9423 0C21.6424 0 27.8846 6.24218 27.8846 13.9423ZM20.9663 8.66108C20.4559 8.15063 19.6283 8.15063 19.1178 8.66108C19.1055 8.67341 19.0939 8.68647 19.0831 8.70018L13.0315 16.4114L9.38296 12.7628C8.87251 12.2524 8.0449 12.2524 7.53445 12.7628C7.024 13.2733 7.024 14.1009 7.53445 14.6113L12.1466 19.2235C12.6571 19.734 13.4847 19.734 13.9952 19.2235C14.0065 19.2122 14.0173 19.2002 14.0273 19.1876L20.9849 10.4907C21.4767 9.97889 21.4705 9.16527 20.9663 8.66108Z" fill="#6524EB" />
                    </svg></span>
                    <span className={styles.itemText}>Captures Leads</span>
                  </li>
                  <p className={styles.bulletTagline}>
                    On its own, Automatically & 24x7
                  </p>
                </ul>


                <p
                  className={styles.skipText}
                  onClick={() => {
                    // GA4: user skipped the intro slides straight to auth.
                    trackEvent("onboarding_skip_click", { screen: "onboarding_step1" });
                    navigate("/login", { replace: true });
                  }}
                >Skip </p>
                <div className={styles.BtnDiv}>
                  <AnimatedButton
                    label={
                      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        How it Works
                        {/* <svg width="17" height="12" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M0 6C0 5.63181 0.319797 5.33333 0.714286 5.33333L13.9249 5.33333L9.42999 1.13807C9.15104 0.877721 9.15104 0.455609 9.42999 0.19526C9.70893 -0.0650902 10.1612 -0.0650902 10.4401 0.19526L16.1544 5.52859C16.4334 5.78894 16.4334 6.21105 16.1544 6.4714L10.4401 11.8047C10.1612 12.0651 9.70893 12.0651 9.42999 11.8047C9.15104 11.5444 9.15104 11.1223 9.42999 10.8619L13.9249 6.66666L0.714286 6.66666C0.319797 6.66666 0 6.36819 0 6Z" fill="white" />
                        </svg> */}

                      </span>
                    }
                    positionType=""
                    width="90%"
                    bottom="10%"
                    onClick={handleNextStep}

                  />
                </div>
              </div>

            </div>
          )}

          {/* Step 2 */}
          {!isLoginRoute && currentStep === 2 && (
            <div className={`${styles.contentBox2} ${stepMotionClass}`}>
              <div className={styles.wrapper}>
                <div className={styles.titlBar}>
                  <h4>
                    Set Up in <span className={styles.blackText}>Minutes</span>
                  </h4>
                  <h4>
                    <span className={styles.blackText}>Test</span> Before You Pay.
                  </h4>
                  {/* <h5>Test Before You Pay.</h5> */}
                </div>
                <div className={styles.decBar}>
                  <p>
                    No hardware. No technical setup. Hear how it answers your calls —  <span> then decide.</span>
                  </p>
                </div>
                <ul className={styles.stepIconList}>
                  <li>
                    <span className={styles.stepIconBubble} aria-hidden="true">
                      <img
                        src="svg/onboarding-step2/Mic.svg"
                        alt=""
                        className={styles.stepIconImg}
                      />
                    </span>
                    <span className={styles.itemText}>Talk to your AI receptionist</span>
                  </li>
                  <li>
                    <span className={styles.stepIconBubble} aria-hidden="true">
                      <img
                        src="svg/onboarding-step2/Check-square.svg"
                        alt=""
                        className={styles.stepIconImg}
                      />
                    </span>
                    <span className={styles.itemText}>Review how it works for you</span>
                  </li>
                  <li>
                    <span className={styles.stepIconBubble} aria-hidden="true">
                      <img
                        src="svg/onboarding-step2/Arrow-up-right.svg"
                        alt=""
                        className={styles.stepIconImg}
                      />
                    </span>
                    <span className={styles.itemText}>Go live when you&apos;re ready</span>
                  </li>
                  {/* <p className={styles.bulletTagline}>
                    Your receptionist never calls in sick.
                  </p> */}
                </ul>

                <div className={styles.BtnDiv}>
                  <AnimatedButton
                    label={
                      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        Build My Receptionist
                        {/* <svg width="17" height="12" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M0 6C0 5.63181 0.319797 5.33333 0.714286 5.33333L13.9249 5.33333L9.42999 1.13807C9.15104 0.877721 9.15104 0.455609 9.42999 0.19526C9.70893 -0.0650902 10.1612 -0.0650902 10.4401 0.19526L16.1544 5.52859C16.4334 5.78894 16.4334 6.21105 16.1544 6.4714L10.4401 11.8047C10.1612 12.0651 9.70893 12.0651 9.42999 11.8047C9.15104 11.5444 9.15104 11.1223 9.42999 10.8619L13.9249 6.66666L0.714286 6.66666C0.319797 6.66666 0 6.36819 0 6Z" fill="white" />
                        </svg> */}

                      </span>
                    }
                    positionType=""
                    width="90%"
                    bottom="10%"
                    onClick={handleNextStep}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3 — ab /login route pe */}
          {isLoginRoute && (
            // authEnter = screen khulne par cheezen ek-ek karke aati hain;
            // emailMode = "Sign In with Email" ke baad input/button turant aayen
            // (sirf animation ke delay badalta hai, logic nahi).
            <div
              className={`${styles.contentBox2} ${styles.authEnter} ${isEmailMode ? styles.emailMode : ""}`}
            >
              <div className={styles.wrapper}>
                <div className={styles.titlBar}>
                  <h4>Create Your</h4>
                  <h5>AI Receptionist</h5>
                </div>
                <div className={styles.decBar}>
                  <p>
                    Sign in to build and test your AI receptionist for <span>your business</span>
                  </p>
                </div>

                {/* EMAIL INPUT */}
                <div
                  className={`${styles.emailDiv} ${isEmailMode ? styles.show : styles.hide}`}
                >
                  <input
                    className={styles.inputdiv}
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value.toLowerCase().trim());
                      setEmailError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !isSendingOtp)
                        handleSendOtpFromOnboarding();
                    }}
                  />
                  {emailError && (
                    <p className={styles.inlineError}>{emailError}</p>
                  )}
                </div>

                {/* PRIMARY AUTH BUTTON — Apple on Apple devices, Google elsewhere */}
                {showApple ? (
                  <div
                    onClick={!isLoadingA ? handleAppleSignIn : undefined}
                    className={`${styles.BtnDiv3} ${isEmailMode ? styles.hide : styles.show} ${isLoadingA ? styles.disabled : ""}`}
                  >
                    <div className={styles.appleSignUp}>
                      {!isLoadingA ? (
                        <>
                          <img src="/svg/apple_2.svg" alt="apple" />
                          <p className={styles.btnText}>Sign up with Apple</p>
                        </>
                      ) : (
                        <Loader size="30" />
                      )}
                    </div>
                  </div>
                ) : window.ReactNativeWebView ? (
                  // App WebView — One Tap support nahi, purana redirect flow
                  <div
                    onClick={!isLoadingG ? chatkefun : undefined}
                    className={`${styles.BtnDiv3} ${isEmailMode ? styles.hide : styles.show} ${isLoadingG ? styles.disabled : ""}`}
                  >
                    <div className={styles.googleSignUp}>
                      {!isLoadingG ? (
                        <>
                          <img src="/svg/Google-icon.svg" alt="Google" />
                          <p className={styles.btnTextDark}>Sign in with Google</p>
                        </>
                      ) : (
                        <Loader size="30" />
                      )}
                    </div>
                  </div>
                ) : (
                  // Web browser — Google One Tap / GIS button
                  <div
                    className={`${styles.BtnDiv3} ${isEmailMode ? styles.hide : styles.show}`}
                  >
                    {isLoadingG ? (
                      <Loader size="30" />
                    ) : (
                      <div
                        ref={googleBtnRef}
                        style={{ display: "flex", justifyContent: "center", width: "100%" }}
                      />
                    )}
                  </div>
                )}

                {/* SEND OTP BUTTON */}
                <div
                  className={`${styles.BtnDiv3} ${isEmailMode ? styles.show : styles.hide}`}
                >
                  <AnimatedButton
                    label="Send One Time Password"
                    width="100%"
                    positionType="relative"
                    onClick={handleSendOtpFromOnboarding}
                    isLoading={isSendingOtp}
                  />
                </div>

                <div
                  className={styles.divider}
                  onClick={() => {
                    if (!isEmailMode) trackEvent("sign_up_start", { method: "email" });
                    setIsEmailMode(true);
                  }}
                >
                  <hr className={styles.line} />
                  {/* key: text badalne par span naya banta hai, to naya text
                      fade hokar aata hai. */}
                  <span key={isEmailMode ? "or" : "email"} className={styles.text}>
                    {isEmailMode ? "Or continue with" : "Sign In with Email"}
                  </span>
                  <hr className={styles.line} />
                </div>

                {/*
                  SECONDARY AUTH ICON — Google sign-in.
                  Visibility rules:
                    - showApple=true (iOS device, Apple is the primary
                      large button when !isEmailMode):
                        Google is the only other option, so show it
                        as the secondary icon in BOTH modes — under
                        the Apple button on the entry screen, and
                        under "Or continue with" in email mode.
                    - showApple=false (non-Apple device, Google IS the
                      primary large button when !isEmailMode):
                        Showing Google again here would duplicate the
                        primary CTA on the entry screen — user reported
                        "doodo baar kyu dikhana." Gate it to ONLY render
                        when the primary is hidden (isEmailMode=true),
                        so non-Apple users still have a Google entry
                        point in email mode but don't see the icon twice
                        on the landing screen.
                */}
                {(showApple || isEmailMode) && (
                  <div className={styles.withLogin}>
                    {window.ReactNativeWebView ? (
                      // App WebView — purana redirect flow
                      <div
                        onClick={!isLoadingG ? chatkefun : undefined}
                        className={isLoadingG ? styles.disabled : ""}
                      >
                        {!isLoadingG ? (
                          <img src="/svg/Google-icon.svg" alt="Sign in with Google" />
                        ) : (
                          <Loader size="30" />
                        )}
                      </div>
                    ) : isLoadingG ? (
                      <Loader size="30" />
                    ) : (
                      // Web browser — Google One Tap / GIS button
                      <div
                        ref={googleBtnRef2}
                        style={{ display: "flex", justifyContent: "center", width: "100%" }}
                      />
                    )}
                  </div>
                )}

                {/* <p className={styles.PrivaceTerms}>
                                    By creating an account, you agree to the Rexpt&nbsp;
                                    <a href="https://www.rexpt.in/Terms-Condition" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
                                    &nbsp;and&nbsp;
                                    <a href="https://www.rexpt.in/Privacy-Policy" target="_blank" rel="noopener noreferrer">Privacy Policy&nbsp;</a>
                                    We'll send you updates about our services. You can unsubscribe anytime.
                                </p> */}
                <p className={styles.PrivaceTerms}>
                  By creating an account, you agree to <br />&nbsp; Rexpt
                  <a
                    href="https://www.rxpt.ai/terms-condition"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms & Conditions
                  </a>
                  &nbsp;&amp;&nbsp;
                  <a
                    href="https://www.rxpt.ai/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                  {/* &nbsp;&amp; consent to receive email & SMS marketing updates,
                  setup instructions, and transactional system alerts from
                  Rexpt. Message and data rates may apply. Message frequency
                  varies. Reply STOP to opt out from marketing. */}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className={`${styles.StartMain} ${introFadeClass}`}>
          <div className={styles.floatingDiv}>
            {Array.from({ length: 11 }, (_, i) => (
              <img
                key={i}
                src={`images/Ellipse ${i + 1}.png`}
                alt={`Ellipse ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
      {/* Step indicator sirf / pe (2 steps). /login auth screen pe nahi. */}
      {!isLoginRoute && (
        <div className={`${styles.StepsDiv} ${introDotsClass}`}>
          {[1, 2].map((step) => (
            <span
              key={step}
              onClick={() => goToStep(step)}
              // Dot turant badle, content ke bahar jaane ka intezaar na kare.
              className={(leaving ? leaving.to : currentStep) === step ? styles.activeStep : ""}
            >
              {step}
            </span>
          ))}
        </div>
      )}
      {showPopup && (
        <PopUp
          type={popupType}
          onClose={() => setShowPopup(false)}
          message={popupMessage}
          renderHTML={true}
        />
      )}
    </div>
  );
};

export default Onboarding;
